/**
 * Pairing Redux Slice
 *
 * Manages state for NFC product pairing with immediate workflow.
 * Uses gateway injection pattern for testability.
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { RootState, StoreExtraDependencies } from '../index';
import type {
  PairingCode,
  PairingResult,
  ProductInfo,
  OwnerNfcScanContextResponse,
} from '@/domain/ports/IPairingGateway';
import { PairingError } from '@/domain/ports/IPairingGateway';

/**
 * Pairing state structure
 */
export interface PairingState {
  status: 'idle' | 'parsing' | 'ready' | 'pairing' | 'success' | 'error';
  nfcUrl: string | null;
  product: ProductInfo | null;
  loading: boolean;
  error: {
    code: string;
    message: string;
  } | null;
  // Success state fields (only set when status is 'success')
  linkedNfcId?: string;
  pairedAt?: string;
  itemNumber?: number;
  // Scan context state
  scanContext: OwnerNfcScanContextResponse | null;
  scanContextLoading: boolean;
  scanContextError: string | null;
}

const initialState: PairingState = {
  status: 'idle',
  nfcUrl: null,
  product: null,
  loading: false,
  error: null,
  scanContext: null,
  scanContextLoading: false,
  scanContextError: null,
};

/**
 * Async Thunk: Pair immediately with 2-word code
 */
export const pairImmediatelyAsync = createAsyncThunk<
  PairingResult,
  { nfcUrl: string; code: PairingCode },
  { extra: StoreExtraDependencies; rejectValue: { code: string; message: string } }
>('pairing/pairImmediately', async ({ nfcUrl, code }, { extra, rejectWithValue }) => {
  try {
    const { pairingGateway } = extra;
    return await pairingGateway.pairImmediately(nfcUrl, code);
  } catch (error) {
    if (error instanceof PairingError) {
      return rejectWithValue({
        code: error.code,
        message: error.message,
      });
    }
    return rejectWithValue({
      code: 'UnknownError',
      message: error instanceof Error ? error.message : 'Unknown error',
    });
  }
});

/**
 * Async Thunk: Unpair item from Trophy Room
 *
 * Calls the future endpoint POST /api/v1/owner/trophy-room/items/:itemId/unpair
 * This endpoint is planned for Story 3.6.b (backend) and will be fully integrated
 * in Story 3.6.d (frontend).
 */
export const unpairItemAsync = createAsyncThunk<
  void,
  { itemId: string },
  { extra: StoreExtraDependencies; rejectValue: { code: string; message: string } }
>('pairing/unpairItem', async ({ itemId }, { extra, rejectWithValue }) => {
  try {
    const { pairingGateway } = extra;
    await pairingGateway.unpairItem(itemId);
  } catch (error) {
    if (error instanceof PairingError) {
      return rejectWithValue({
        code: error.code,
        message: error.message,
      });
    }
    return rejectWithValue({
      code: 'UnknownError',
      message: error instanceof Error ? error.message : 'Unknown error',
    });
  }
});

/**
 * Async Thunk: Get scan context
 */
export const getScanContextAsync = createAsyncThunk<
  OwnerNfcScanContextResponse,
  string,
  { extra: StoreExtraDependencies; rejectValue: string }
>('pairing/getScanContext', async (nfcUrl, { extra, rejectWithValue }) => {
  try {
    const { pairingGateway } = extra;
    return await pairingGateway.getScanContext(nfcUrl);
  } catch (error) {
    if (error instanceof PairingError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue(error instanceof Error ? error.message : 'Network error');
  }
});

/**
 * Pairing Slice
 */
const pairingSlice = createSlice({
  name: 'pairing',
  initialState,
  reducers: {
    /**
     * Set NFC URL when page loads
     */
    setNfcUrl(state, action: PayloadAction<string>) {
      state.status = 'ready';
      state.nfcUrl = action.payload;
      state.product = null;
      state.loading = false;
      state.error = null;
    },

    /**
     * Reset pairing state
     */
    resetPairing() {
      return initialState;
    },

    /**
     * Clear scan context
     */
    clearScanContext(state) {
      state.scanContext = null;
      state.scanContextError = null;
    },
  },

  extraReducers: (builder) => {
    // Immediate Pairing
    builder
      .addCase(pairImmediatelyAsync.pending, (state) => {
        if (state.status === 'ready') {
          state.status = 'pairing';
          state.loading = true;
          state.error = null;
        }
      })
      .addCase(pairImmediatelyAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.product = action.payload.productInfo;
        state.linkedNfcId = action.payload.linkedNfcId;
        state.pairedAt = action.payload.pairedAt;
        state.itemNumber = action.payload.itemNumber;
        state.loading = false;
        state.error = null;
      })
      .addCase(pairImmediatelyAsync.rejected, (state, action) => {
        state.status = 'error';
        state.loading = false;
        state.error = action.payload || { code: 'UnknownError', message: 'Unknown error' };
      });

    // Scan Context
    builder
      .addCase(getScanContextAsync.pending, (state) => {
        state.scanContextLoading = true;
        state.scanContextError = null;
      })
      .addCase(getScanContextAsync.fulfilled, (state, action) => {
        state.scanContext = action.payload;
        state.scanContextLoading = false;
        state.scanContextError = null;
      })
      .addCase(getScanContextAsync.rejected, (state, action) => {
        state.scanContextLoading = false;
        state.scanContextError = action.payload || 'Failed to load scan context';
      });
  },
});

// Actions
export const {
  setNfcUrl,
  resetPairing,
  clearScanContext,
} = pairingSlice.actions;

// Selectors
export const selectPairing = (state: RootState) => state.pairing;

// Reducer
export default pairingSlice.reducer;
